/**
 * Bilder IM Text — umflossen statt gestapelt.
 *
 * Der Editor kannte Bilder bisher nur als eigenen Baustein: ein `mj-image`
 * belegt eine ganze Zeile, Text steht darüber oder darunter, nie daneben. Wer
 * ein Foto neben den Absatz stellen will, kommt damit nicht ans Ziel.
 *
 * Was in einer E-Mail wirklich geht — und was nicht:
 *
 *   Frei positionieren (an eine beliebige Stelle ziehen, Bild über Text)
 *     geht NICHT. Outlook rendert mit der Word-Maschine; `position:absolute`
 *     wird dort ignoriert, in Gmail wird es beim Ausliefern entfernt. Jedes
 *     Werkzeug, das eine solche Fläche anbietet, backt am Ende doch wieder
 *     eine Tabelle daraus.
 *
 *   Umfluss (Bild links oder rechts, Text fließt drumherum)
 *     geht. Der Weg ist ein `<img>` INNERHALB des Textes, mit `float` für die
 *     modernen Clients und dem alten `align`-Attribut für Word/Outlook —
 *     dieses Attribut ist das einzige, das die Word-Maschine für den Umfluss
 *     auswertet. Beides zusammen deckt praktisch jeden Posteingang ab.
 *
 *   Bild neben Text als Spalte
 *     geht ebenfalls und ist am Telefon die bessere Wahl (die Spalten stapeln
 *     dort untereinander). Dafür gibt es den Baustein „Bild neben Text".
 *
 * Diese Datei macht die mittlere Variante bedienbar: Ein Bild aus der
 * Bibliothek landet im Text, ist danach anklickbar wie jeder andere Baustein
 * und lässt sich über die Einstellungen umstellen — links, rechts, eigene
 * Zeile — sowie in der Breite ändern. Im Text selbst kann es an eine andere
 * Stelle geschoben werden, weil es Teil des Textes ist.
 */

/** Der eigene Komponententyp. */
export const TYP = 'bild-im-text';

/**
 * Nutzbare Breite einer Spalte: 600 px Nachrichtenbreite, abzüglich der
 * 24 px Innenabstand auf beiden Seiten, die alle Kanzlei-Bausteine setzen.
 * Aus ihr werden die Pixelbreiten gerechnet, die Outlook braucht.
 */
export const SPALTENBREITE = 552;

/**
 * Anordnung des Bildes im Text.
 *
 * `left`/`right` erzeugen den Umfluss, `block` stellt das Bild in eine eigene
 * Zeile — dann verhält es sich wie ein Bildbaustein, bleibt aber Teil des
 * Absatzes und wird mit ihm verschoben.
 */
export const ANORDNUNGEN = [
  { value: 'left', name: 'Links, Text fließt rechts' },
  { value: 'right', name: 'Rechts, Text fließt links' },
  { value: 'block', name: 'Eigene Zeile' },
];

/** Breiten als Anteil der Spalte — verständlicher als Pixelangaben. */
export const BREITEN = [
  { value: '25', name: 'Viertel' },
  { value: '33', name: 'Drittel' },
  { value: '50', name: 'Halb' },
  { value: '66', name: 'Zwei Drittel' },
  { value: '100', name: 'Volle Breite' },
];

/**
 * Rechnet einen Anteil in die Pixelbreite um, die als HTML-Attribut gesetzt
 * wird. Outlook liest ausschließlich dieses Attribut; fehlt es, skaliert die
 * Word-Maschine das Bild auf 120 DPI hoch und sprengt das Layout.
 *
 * @param {string|number} anteil In Prozent.
 * @returns {number}
 */
export function pixelbreite(anteil) {
  const prozent = Math.min(Math.max(Number(anteil) || 50, 5), 100);
  return Math.round((SPALTENBREITE * prozent) / 100);
}

/**
 * Baut die Attribute für ein Bild im Text.
 *
 * Warum drei Angaben für eine Breite:
 *   width="276"        → Outlook (HTML-Attribut, Pixel, nichts anderes zählt)
 *   style width:50%    → moderne Clients, damit das Bild am Telefon mitschrumpft
 *   max-width:276px    → damit es auf dem Rechner nicht über seine Größe wächst
 *
 * @param {string} anordnung 'left' | 'right' | 'block'
 * @param {string|number} anteil Breite in Prozent der Spalte.
 * @returns {{ align: string|null, width: string, style: string }}
 */
export function darstellung(anordnung, anteil) {
  const px = pixelbreite(anteil);
  const prozent = Math.min(Math.max(Number(anteil) || 50, 5), 100);

  const grund = `width:${prozent}%;max-width:${px}px;height:auto;border:0;`;

  if (anordnung === 'left') {
    return {
      // `align` ist veraltetes HTML — und genau deshalb unverzichtbar: die
      // Word-Maschine in Outlook wertet `float` nicht aus, `align` schon.
      align: 'left',
      width: String(px),
      style: `${grund}float:left;margin:4px 18px 10px 0;`,
    };
  }
  if (anordnung === 'right') {
    return {
      align: 'right',
      width: String(px),
      style: `${grund}float:right;margin:4px 0 10px 18px;`,
    };
  }
  return {
    align: null,
    width: String(px),
    // display:block verhindert den 3-4 px Spalt, den mehrere Clients unter
    // ein Bild setzen, das als Textzeile behandelt wird.
    style: `${grund}display:block;margin:10px 0;`,
  };
}

/**
 * Liest die Anordnung aus vorhandenen Attributen zurück — nötig beim Öffnen
 * einer gespeicherten Vorlage, damit die Einstellungen den Ist-Zustand zeigen
 * und nicht eine Vorgabe.
 *
 * @param {Record<string, any>} attribute
 * @returns {string}
 */
export function anordnungLesen(attribute) {
  const stil = String(attribute?.style ?? '');
  if (/float\s*:\s*left/i.test(stil)) return 'left';
  if (/float\s*:\s*right/i.test(stil)) return 'right';
  const ausrichtung = String(attribute?.align ?? '').toLowerCase();
  if (ausrichtung === 'left' || ausrichtung === 'right') return ausrichtung;
  return 'block';
}

/**
 * Liest die Breite als Anteil zurück. Bevorzugt die Prozentangabe im Stil;
 * ein Bild aus fremder Hand kennt sie nicht, dann wird aus der Pixelbreite
 * gerechnet.
 *
 * @param {Record<string, any>} attribute
 * @returns {string}
 */
export function anteilLesen(attribute) {
  const stil = String(attribute?.style ?? '');
  const prozent = /width\s*:\s*(\d+(?:\.\d+)?)\s*%/i.exec(stil)?.[1];
  if (prozent) return String(naechsterAnteil(Number(prozent)));

  const px = Number(String(attribute?.width ?? '').replace(/[^\d]/g, ''));
  if (px > 0) return String(naechsterAnteil((px / SPALTENBREITE) * 100));

  return '50';
}

/**
 * Rundet auf die nächstliegende angebotene Breite. Ohne das stünde in der
 * Auswahl nichts, sobald eine Vorlage einen krummen Wert mitbringt — und die
 * Bedienerin sähe ein leeres Feld statt der tatsächlichen Breite.
 *
 * @param {number} prozent
 * @returns {number}
 */
function naechsterAnteil(prozent) {
  const werte = BREITEN.map((eintrag) => Number(eintrag.value));
  return werte.reduce((beste, wert) =>
    Math.abs(wert - prozent) < Math.abs(beste - prozent) ? wert : beste,
  );
}

/**
 * Wählt die Anfangsbreite für ein frisch eingesetztes Bild.
 *
 * Genommen wird die größte angebotene Breite, die das Bild noch abdeckt — ein
 * 180 px breites Logo auf halbe Spaltenbreite zu ziehen macht es unscharf,
 * und im Posteingang ist das der erste Eindruck. Kennt die Bibliothek die
 * Maße nicht, bleibt es bei der Hälfte.
 *
 * @param {number} [naturbreite] Tatsächliche Breite der Bilddatei in Pixeln.
 * @returns {string} Anteil in Prozent, passend zu BREITEN.
 */
export function anteilFuerNaturbreite(naturbreite) {
  if (!naturbreite || naturbreite <= 0) return '50';

  const passend = BREITEN.map((eintrag) => eintrag.value)
    // Nie breiter als die halbe Spalte: ein Umfluss, bei dem für den Text
    // eine Handbreit bleibt, ist keiner.
    .filter((wert) => Number(wert) <= 50)
    .filter((wert) => pixelbreite(wert) <= naturbreite);

  return passend.length ? passend[passend.length - 1] : BREITEN[0].value;
}

/**
 * MJML-taugliches `<img>` für den Textbaustein.
 *
 * @param {object} bild
 * @param {string} bild.adresse
 * @param {string} bild.beschriftung
 * @param {string} [bild.anordnung]
 * @param {string|number} [bild.anteil]
 * @returns {string}
 */
export function bildMarkup({ adresse, beschriftung, anordnung = 'left', anteil = 50 }) {
  const d = darstellung(anordnung, anteil);
  return (
    `<img src="${maskieren(adresse)}" alt="${maskieren(beschriftung)}" width="${d.width}"` +
    `${d.align ? ` align="${d.align}"` : ''} style="${d.style}" />`
  );
}

/**
 * Maskiert einen Attributwert.
 *
 * Ein Anführungszeichen im Alt-Text — „Foto »Neubau« 2026" ist ein
 * Dateiname wie jeder andere — würde das Tag mitten im Wort beenden und den
 * Rest zu Attributen machen. Maskiert statt gelöscht, weil der Alt-Text bei
 * blockierten Bildern in Outlook der gesamte sichtbare Inhalt ist: Er soll
 * lesbar bleiben und nicht stillschweigend Zeichen verlieren.
 *
 * @param {string} wert
 * @returns {string}
 */
function maskieren(wert) {
  return String(wert ?? '')
    // Das & zuerst, sonst würde es die eigenen Ersetzungen erneut treffen.
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');
}

/**
 * Meldet den Komponententyp beim Editor an.
 *
 * Der Schlüssel ist `textable`: ohne diese Angabe behandelt GrapesJS ein
 * Element im Text als unteilbaren Teil des Textes — anklicken ließe sich dann
 * nur der ganze Absatz, und das Bild hätte keine eigenen Einstellungen. Mit
 * ihr ist das Bild ein eigener Baustein, der im Text lebt und sich darin
 * verschieben lässt.
 *
 * @param {any} editor
 */
export function bildImTextRegistrieren(editor) {
  editor.DomComponents.addType(TYP, {
    // Greift nur für echte `img`-Elemente. Ein Bildbaustein ist `mj-image`
    // und bleibt unberührt.
    isComponent: (/** @type {any} */ element) =>
      element?.tagName === 'IMG' ? { type: TYP } : undefined,

    model: {
      defaults: {
        tagName: 'img',
        void: true,
        textable: true,
        droppable: false,
        highlightable: true,
        name: 'Bild im Text',
        anordnung: 'left',
        anteil: '50',
        traits: [
          {
            type: 'select',
            name: 'anordnung',
            label: 'Anordnung',
            changeProp: true,
            options: ANORDNUNGEN,
          },
          {
            type: 'select',
            name: 'anteil',
            label: 'Breite',
            changeProp: true,
            options: BREITEN,
          },
          // Ohne Alt-Text bleibt bei blockierten Bildern in Outlook eine
          // leere Fläche — dasselbe Argument wie beim Bildbaustein.
          { type: 'text', name: 'alt', label: 'Bildbeschreibung (wichtig)' },
          { type: 'text', name: 'src', label: 'Bildadresse' },
        ],
      },

      /** @this {any} */
      init() {
        const attribute = this.getAttributes();
        // Still setzen: das ist der Ist-Zustand, keine Änderung. Ein lautes
        // Setzen würde eine frisch geöffnete Vorlage sofort als bearbeitet
        // gelten lassen und beim Weggehen unnötig nachfragen.
        this.set(
          { anordnung: anordnungLesen(attribute), anteil: anteilLesen(attribute) },
          { silent: true },
        );
        this.on('change:anordnung change:anteil', this.darstellungSchreiben);
      },

      /**
       * Überträgt die Einstellungen in die Attribute des Bildes.
       * @this {any}
       */
      darstellungSchreiben() {
        const d = darstellung(this.get('anordnung'), this.get('anteil'));
        // `align` muss verschwinden, wenn es keinen Umfluss mehr gibt —
        // sonst bliebe in Outlook der alte Umfluss stehen, während der
        // Editor bereits eine eigene Zeile zeigt.
        if (!d.align) this.removeAttributes('align');
        this.addAttributes({
          width: d.width,
          style: d.style,
          ...(d.align ? { align: d.align } : {}),
        });
      },
    },
  });
}

/**
 * Sucht den Textbaustein, in den ein Bild gehört.
 *
 * @param {any} editor
 * @returns {any|null}
 */
export function textbausteinFinden(editor) {
  let aktuell = editor.getSelected();
  while (aktuell) {
    if (aktuell.get?.('type') === 'mj-text') return aktuell;
    // Ein bereits eingesetztes Bild im Text: dessen Absatz ist gemeint.
    aktuell = aktuell.parent?.();
  }
  return null;
}

/**
 * Setzt ein Bild in einen Textbaustein ein.
 *
 * Zwei Wege, je nachdem, ob gerade im Text geschrieben wird:
 *
 *  - Der Cursor steht im Text → das Bild kommt genau dorthin. Wer vorher eine
 *    Stelle angeklickt hat, erwartet es dort und nirgendwo sonst.
 *  - Sonst → an den Anfang des Absatzes. Das ist die Stelle, an der ein
 *    umflossenes Bild seinen Zweck erfüllt: der Text fließt ab dem Bild,
 *    nicht erst ab der Mitte des Absatzes.
 *
 * @param {any} editor
 * @param {any} textbaustein Komponente vom Typ `mj-text`.
 * @param {object} bild
 * @param {string} bild.adresse
 * @param {string} bild.beschriftung
 * @param {string} [bild.anordnung]
 * @param {string|number} [bild.anteil]
 * @returns {'cursor'|'anfang'|null} Wo es gelandet ist — null, wenn gar nicht.
 */
export function bildInTextEinsetzen(editor, textbaustein, bild) {
  if (!textbaustein) return null;
  const markup = bildMarkup(bild);

  const dokument = editor.Canvas?.getDocument?.();
  const aktiv = dokument?.activeElement;
  const imTextGeschrieben =
    aktiv &&
    /** @type {HTMLElement} */ (aktiv).isContentEditable &&
    textbaustein.view?.el?.contains?.(aktiv);

  if (imTextGeschrieben) {
    // insertHTML ist veraltet, aber in contenteditable weiterhin der einzige
    // Weg, der die Rücknahme-Funktion des Editors intakt lässt — derselbe
    // Grund wie im Einfüge-Filter.
    dokument.execCommand('insertHTML', false, markup);
    return 'cursor';
  }

  const innen =
    typeof textbaustein.getInnerHTML === 'function' ? textbaustein.getInnerHTML() : '';
  textbaustein.components(`${markup}${innen}`);

  // Das eingesetzte Bild auswählen: seine Einstellungen — Seite und Breite —
  // stehen dann sofort rechts, statt erst gesucht werden zu müssen.
  const eingesetzt = textbaustein
    .components()
    .filter?.((/** @type {any} */ teil) => teil.get?.('type') === TYP)?.[0];
  if (eingesetzt) editor.select(eingesetzt);

  return 'anfang';
}
